import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { venueImages } from '../../../constants/realVenueData';
import { resolveUserDisplayName } from '../../../utils/userDisplayName';

// Figma asset URLs (kept for chevron, search - icons only)
const imgChevronDown = "https://www.figma.com/api/mcp/asset/a4281d31-c2a4-4d6c-b5b8-1ad0ca165f5e";
const imgSearch = "https://www.figma.com/api/mcp/asset/29b54888-60a6-4557-86c5-62d0df08b42f";

// Fallback venue image (smaller, like venue listing thumbnail)
const FALLBACK_VENUE_IMAGE = 'https://images.unsplash.com/photo-1508921340878-ba53e1f016ec?w=80&h=80&fit=crop';

const DashboardHeader = ({ venueName, venueImageUrl, userProfile, currentUser, onRefreshDashboard, dashboardRefreshing }) => {
  const { logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Use venue image from API/venue details, or fallback (same as venue listing)
  const venueImage = venueImageUrl || userProfile?.venue_image || venueImages?.[0] || FALLBACK_VENUE_IMAGE;

  // Profile: Google image if available, else initials from account name
  const profileImageUrl = userProfile?.profile_image || userProfile?.profile_picture_url || currentUser?.photoURL;
  const displayName = resolveUserDisplayName(userProfile, currentUser) || currentUser?.email || '';
  const initials = displayName
    ? displayName.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()
    : (currentUser?.email?.[0] || '?').toUpperCase();

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleCancelLogout = () => setShowLogoutModal(false);

  const handleConfirmLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && showLogoutModal && !isLoggingOut) {
      setShowLogoutModal(false);
    }
  }, [showLogoutModal, isLoggingOut]);

  useEffect(() => {
    if (showLogoutModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [showLogoutModal, handleKeyDown]);

  return (
    <div className="dashboard-header">
      <div className="header-left-container">
        <div className="header-venue-card">
          <div className="header-venue-avatar-container">
            <img src={venueImage} alt="Venue" className="header-venue-avatar" />
          </div>
          <div className="header-venue-name-group">
            <span className="header-venue-name">{venueName || 'Kasa Coffee'}</span>
            {userProfile?.owned_venues?.length > 1 && (
              <div className="header-chevron-down">
                <img src={imgChevronDown} alt="Dropdown" className="header-chevron-icon" />
              </div>
            )}
          </div>
        </div>
        <div className="search-bar-hidden">
          <div className="search-input-container">
            <div className="search-input-wrapper">
              <div className="search-left-content">
                <div className="search-icon-container">
                  <img src={imgSearch} alt="Search" className="search-icon" />
                </div>
                <div className="search-placeholder">
                  <span>Search something</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="header-right">
          {onRefreshDashboard ? (
            <button
              type="button"
              className="dashboard-refresh-button"
              onClick={onRefreshDashboard}
              disabled={dashboardRefreshing}
              title="Refresh dashboard data"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {dashboardRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          ) : null}
          <button
            className="dashboard-logout-button"
            onClick={handleLogoutClick}
            disabled={isLoggingOut}
            title="Logout"
          >
            <svg className="logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Logout
          </button>
          <div className="notification-icon" title="Notifications">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="user-profile">
            <div className="profile-avatar">
              {profileImageUrl && (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling?.classList.add('visible');
                  }}
                />
              )}
              <span className={`profile-avatar-initials ${!profileImageUrl ? 'visible' : ''}`} aria-hidden={!!profileImageUrl}>
                {initials}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Logout confirmation modal */}
      {showLogoutModal && (
        <div
          className="dash-logout-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !isLoggingOut) handleCancelLogout(); }}
        >
          <div className="dash-logout-modal" onClick={(e) => e.stopPropagation()}>
            {/* Icon */}
            <div className="dash-logout-icon-wrap">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="#b50001" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17l5-5-5-5" stroke="#b50001" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12H9" stroke="#b50001" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h2 className="dash-logout-title">Log out of dashboard?</h2>
            <p className="dash-logout-message">
              You'll need to sign in again to manage your venue.
            </p>

            <div className="dash-logout-actions">
              <button
                type="button"
                className="dash-logout-btn dash-logout-btn--cancel"
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dash-logout-btn dash-logout-btn--confirm"
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <>
                    <svg className="dash-logout-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.07 19.07L16.24 16.24M19.07 4.93L16.24 7.76M4.93 19.07L7.76 16.24M4.93 4.93L7.76 7.76" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Logging out…
                  </>
                ) : 'Log out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;

